import React, { memo, useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { IAIVideoModel } from 'types/aiVideo'
import { isPremiumModel } from 'utils'
import { userSelectors } from 'store/features/user/selectors'
import { groupModels } from 'containers/SubSidebar/shared/GroupedModelDropdown/utils'
import { getCapabilityLabels } from './utils'
import { AI_VIDEO_MODEL_GROUP_META } from './consts'
import { getCreditLabel } from './creditUtils'
import { VIDEO_GROUP_RATINGS, VIDEO_VARIANT_RATINGS } from './videoRatings'
import { getGroupDescription } from './videoDescriptions'
import SectionedModelList from './SectionedModelList'
import { IVideoDropdownModel, IVideoModelGroup } from './types'

interface IProps {
  models: IAIVideoModel[]
  selectedModelCode: string | null
  onSelect: (modelCode: string) => void
  userCurrentPlanId: number
  onInnerSlideUpOpenChange?: (open: boolean) => void
}

const ModelDropdownContent: React.FC<IProps> = ({
  models: inputModels,
  selectedModelCode,
  onSelect,
  userCurrentPlanId,
  onInnerSlideUpOpenChange
}) => {
  const unlimitedAiVideoModels = useSelector(
    userSelectors.unlimitedAiVideoModels
  )

  const unlimitedModelIds = useMemo(
    () => new Set(unlimitedAiVideoModels.map(u => u.aiVideoModelId)),
    [unlimitedAiVideoModels]
  )
  const isUnlimitedById = useCallback(
    (modelId: number) => unlimitedModelIds.has(modelId),
    [unlimitedModelIds]
  )

  const dropdownModels = useMemo<IVideoDropdownModel[]>(
    () =>
      inputModels.map(m => ({
        id: m.id,
        name: m.name,
        selectKey: m.model,
        groupKey: m.code,
        order: m.order,
        isPremium: isPremiumModel(m, userCurrentPlanId),
        capabilityLabels: getCapabilityLabels([m]),
        description: m.description || '',
        creditInfo: getCreditLabel([m], isUnlimitedById),
        rating: VIDEO_VARIANT_RATINGS[m.model] ?? null
      })),
    [inputModels, userCurrentPlanId, isUnlimitedById]
  )

  const groups = useMemo<IVideoModelGroup[]>(() => {
    const baseGroups = groupModels(dropdownModels, AI_VIDEO_MODEL_GROUP_META)
    return baseGroups.map<IVideoModelGroup>(group => {
      const sourceModels = inputModels.filter(m => m.code === group.key)
      const variantModels = group.models as IVideoDropdownModel[]
      return {
        key: group.key,
        label: group.label,
        Icon: group.Icon,
        models: variantModels,
        description:
          getGroupDescription(group.key) || sourceModels[0]?.description || '',
        rating: VIDEO_GROUP_RATINGS[group.key] ?? null,
        creditInfo: getCreditLabel(sourceModels, isUnlimitedById)
      }
    })
  }, [dropdownModels, inputModels, isUnlimitedById])

  const getGroupLabels = useCallback(
    (group: IVideoModelGroup) =>
      getCapabilityLabels(inputModels.filter(m => m.code === group.key)),
    [inputModels]
  )

  const handleSelect = (model: IVideoDropdownModel) => onSelect(model.selectKey)

  return (
    <SectionedModelList
      groups={groups}
      selectedKey={selectedModelCode}
      onSelect={handleSelect}
      getGroupLabels={getGroupLabels}
      onInnerSlideUpOpenChange={onInnerSlideUpOpenChange}
    />
  )
}

export default memo(ModelDropdownContent)
